<template>
  <div class="flex-container">
    <div class="page-title-row">
      <h1 class="page-title">
        Tekil Ürün Ekle
      </h1>
      <a
        :href="educationalVideoUrl"
        class="watch-link"
        target="_blank"
      >
        <img src="@/assets/images/icons/icon-play.svg">
        Eğitim Videosunu İzle
      </a>
    </div>
    <p class="page-description">
      <span>n11 kataloğunda olmayan</span> bir ürünü mağazanıza eklemek
      istiyorsanız ürün bilgilerini girerek satışa açabilirsiniz.
    </p>
    <ProductInfo
      ref="productInfo"
      @fetch-category-attributes="handleFetchCategoryAttributes"
    />
    <ProductFeatures
      v-if="!isOnlyBrandsAttr"
      ref="productFeatures"
      :is-category-selected="isCategorySelected"
      :category-attributes="categoryAttributes"
    />
    <SalesVariants
      ref="salesVariants"
      :category="categoryId"
      :is-category-selected="isCategorySelected"
      :currencies="currencies"
      :category-attributes="categoryAttributes"
    />
    <AdditionalInfo ref="additionalInfo" />
    <RichText
      ref="richText"
      v-model:isReusablePattern="isReusablePattern"
      :content="editorContent"
      :is-has-content="isHasTemplate"
      @open-modal="openSelectedTempModal"
      @update:content="editorContent = $event"
    />
    <DeliveryTemplate ref="deliveryTemplate" />
    <ProductSafetyInformations
      ref="productSafetyInformations"
      :is-nutrition-info-required="isNutritionInfoRequired"
      :category-id="categoryId"
      :is-category-selected="isCategorySelected"
      :is-dropdown-open="true"
    />
    <div class="actions-pane">
      <div class="container">
        <Button
          primary
          @click="onSubmit"
        >
          Onaya Gönder
        </Button>
      </div>
    </div>
  </div>

  <SelectDescriptionTempModal
    ref="selectDescriptionTempModalRef"
    :templates="templates"
    @template-applied="applyTemplateToEditor"
  />
  <SuccessModal ref="singleProductSuccessModalRef" />
</template>

<script setup>
import DeliveryTemplate from './partials/DeliveryTemplate';
import AdditionalInfo from './partials/AdditionalInfo';
import ProductFeatures from './partials/ProductFeatures';
import SalesVariants from './partials/SalesVariants';
import RichText from './partials/RichText';
import ProductInfo from './partials/ProductInfo';
import { ref, nextTick, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { Button } from '@n11/so-ui-components';
import SelectDescriptionTempModal from './partials/SelectDescriptionTempModal.vue';
import SuccessModal from './partials/SuccessModal.vue';
import ProductSafetyInformations from './partials/ProductSafetyInformations.vue';

const store = useStore();

const productInfo = ref([]);
const productFeatures = ref([]);
const salesVariants = ref([]);
const additionalInfo = ref([]);
const richText = ref([]);
const deliveryTemplate = ref(null);
const singleProductSuccessModalRef = ref(null);

const isCategorySelected = ref(false);
const categoryAttributes = ref([]);
const categoryId = ref(null);
const isOnlyBrandsAttr = ref(false);

const currencies = ref({});
const selectDescriptionTempModalRef = ref(null);
const editorContent = ref(null);
const templates = ref([]);
const isHasTemplate = ref(false);
const isReusablePattern = ref(false);
const productSafetyInformations = ref(null);
const isNutritionInfoRequired = ref(false);
const educationalVideoUrl =
  'https://magazadestek.n11.com/egitim-detay/n11katalogunda-olmayan-urun-nasil-eklenir-20770';
const handleFetchCategoryAttributes = async (data) => {
  categoryAttributes.value = [];
  isCategorySelected.value = false;

  await nextTick();
  categoryAttributes.value = data.categoryAttributes;
  categoryId.value = data.id;
  isCategorySelected.value = true;
  isOnlyBrandsAttr.value =
    categoryAttributes.value.length === 1 &&
    categoryAttributes.value.find((attr) => attr.attributeId === 1);

  await getAndCleanDescriptionTemplates(data.id);
};

const getExchanges = async () => {
  try {
    const response = await store.dispatch('categories/getExchangeRates');
    currencies.value = response;
  } catch (error) {
    console.error('Exchange rates yüklenemedi:', error);
    return [];
  }
};

const scrollToElementById = (id) => {
  setTimeout(() => {
    const element = document.getElementById(id);
    if (element) {
      const elementTop =
        element.getBoundingClientRect().top + window.pageYOffset;
      const scrollPosition = elementTop - 100;

      window.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });
    } else {
      console.warn(`Element with id "${id}" not found.`);
    }
  }, 100);
};

const onSubmit = async () => {
  const mapProductsToSkus = (products) => {
    return {
      skus: products.map((product) => {
        const attributes = [];
        if (product.brandId) {
          attributes.push({
            id: 1,
            valueId: null,
            customValue: product.brandId.toString(),
          });
        }

        product.features.forEach((feature) => {
          attributes.push({
            id: feature.feature.attributeId,
            valueId:
              feature.feature.attributeValues.find(
                (val) => val.value === feature.selection,
              )?.id ||
              feature.feature?.currentDropItems.find(
                (val) => val.value === feature.name,
              )?.id ||
              null,
            customValue: feature.selection,
          });
        });

        product.attributes.forEach((attr) => {
          if (attr.attributeId && attr.attributeValueId) {
            attributes.push({
              id: attr.attributeId,
              valueId: attr.attributeValueId,
              customValue: null,
            });
            return;
          }

          const meta = (categoryAttributes.value ?? []).find(
            (m) => m.attributeName === attr.name,
          );

          const valMeta = meta?.attributeValues?.find(
            (v) => v.value === attr.value || v.id === attr.id,
          );

          attributes.push({
            id: meta?.attributeId ?? null,
            valueId: valMeta?.id ?? null,
            customValue: valMeta ? null : (attr.value ?? null),
          });
        });

        return {
          title: product.title,
          description: editorContent.value,
          categoryId: product.categoryId,
          currencyType:
            product.currencyCode === 'TRY' ? 'TL' : product.currencyCode,
          productMainId: product.productMainId,
          preparingDay: parseInt(product.duration),
          shipmentTemplate: product.selectedTemplate?.name,
          maxPurchaseQuantity: product.maxSalesLimit
            ? Number(product.maxSalesLimit)
            : null,
          customTextOptions: product.customProductOptionList.map((e) => e.text),
          stockCode: product.stockCode,
          catalogId: null, // always null
          barcode: product.gtin,
          descriptionTemplateName: isReusablePattern.value
            ? richText?.value.getRichText().data.templateName
            : null,
          quantity: parseInt(product.stock),
          images: product.images.map((img) => ({
            id: img.id,
            order: img.order,
            url: img.url,
          })),
          attributes: attributes,
          listPrice: product.listPrice,
          salePrice: product.salePrice,
          vatRate: parseInt(product.vatRate),
        };
      }),
      safetyInfo: productSafetyInformationsData.safetyInfo,
    };
  };
  // collect information
  const productInfoData = productInfo?.value.getProductInformation();
  const salesVariantInfoData = await salesVariants?.value.getSalesVariant();
  const productFeaturesData =
    await productFeatures?.value?.getProductFeatures();
  const richTextData = richText?.value.getRichText();
  const additionalInfoData = additionalInfo.value.getAdditionalInfo();
  const deliveryTemplateData = deliveryTemplate?.value.getDeliveryTemplate();
  const productSafetyInformationsData =
    productSafetyInformations?.value?.getProductSafetyInformations();

  let firstInvalidField = null;
  let isValid = true;

  const validationChecks = [
    { name: 'productInfoSection', isValid: productInfoData.isValid },
    { name: 'salesVariantInfoSection', isValid: salesVariantInfoData.isValid },
    { name: 'additionalInfoSection', isValid: additionalInfoData.isValid },
    { name: 'richTextSection', isValid: richTextData.isValid },
    { name: 'deliveryTemplateSection', isValid: deliveryTemplateData.isValid },
  ];

  if (!isOnlyBrandsAttr.value) {
    validationChecks.push({
      name: 'productFeaturesSection',
      isValid: productFeaturesData && productFeaturesData.isValid,
    });
  }

  if (productSafetyInformations?.value) {
    const isDropdownOpen = productSafetyInformations.value.isDropdownOpen();
    if (isDropdownOpen) {
      validationChecks.push({
        name: 'productSafetyInformationsSection',
        isValid: productSafetyInformationsData?.isValid ?? true,
      });
    }
  }

  for (const check of validationChecks) {
    if (check && !check.isValid) {
      isValid = false;
      firstInvalidField = check.name;
      break;
    }
  }
  if (isValid) {
    const payload = salesVariantInfoData.data.tableRows.map((s) => {
      return {
        ...productInfoData.data,
        ...(!isOnlyBrandsAttr.value
          ? productFeaturesData.data
          : { features: [] }),
        ...richTextData.data,
        ...deliveryTemplateData.data,
        ...additionalInfoData.data,
        ...s,
        ...salesVariantInfoData.data.currency,
        ...productSafetyInformationsData.data,
      };
    });
    let response = null;
    try {
      response = await store.dispatch(
        'products/getSubmitSingleProduct',
        mapProductsToSkus(payload),
      );
      if (!response.success) {
        const allErrors =
          (response?.errors && Object.keys(response.errors).length > 0
            ? Object.values(response.errors).flatMap((errorObj) =>
                errorObj?.value ? [errorObj.value] : [errorObj],
              )
            : []
          )
            .concat(
              response?.skus?.flatMap((item) =>
                item.errors
                  ? Object.values(item.errors).flatMap((errorObj) =>
                      errorObj?.value ? [errorObj.value] : [errorObj],
                    )
                  : [],
              ),
            )
            .join('<br>') ?? '';
        const hasImageError = allErrors.includes(
          'Kataloğa ürün önerme başarısız oldu. Katalog mesaj All Images are invalid',
        );
        store.commit('common/SET_UPLOAD_IMAGE_SUCCESS', hasImageError);
        store.commit('common/SET_TOASTER_DATA', {
          type: 'error',
          title: '',
          description: allErrors,
        });

        return;
      }
    } catch (e) {
      return store.commit('common/SET_TOASTER_DATA', {
        type: 'error',
        title: '',
        description: e.message,
      });
    }
    if (response && response.success)
      singleProductSuccessModalRef?.value?.openProductSuccessModal();
  } else {
    switch (firstInvalidField) {
      case 'productInfoSection':
        scrollToElementById('productInfoSection');
        break;
      case 'productFeaturesSection':
        scrollToElementById('productFeaturesSection');
        break;
      case 'salesVariantInfoSection':
        scrollToElementById('salesVariantInfoSection');
        break;
      case 'additionalInfoSection':
        scrollToElementById('additionalInfoSection');
        break;
      case 'richTextSection':
        scrollToElementById('richTextSection');
        break;
      case 'deliveryTemplateSection':
        scrollToElementById('deliveryTemplateSection');
        break;
      case 'productSafetyInformationsSection':
        scrollToElementById('productSafetyInformationsSection');
        break;
    }
  }
};

const getAndCleanDescriptionTemplates = async (categoryId) => {
  try {
    const response = await store.dispatch(
      'products/getProductDescription',
      categoryId,
    );
    const rawItems = response.productDescriptionDtos;
    if (rawItems && rawItems.length > 0) {
      isHasTemplate.value = true;
      templates.value = rawItems.map((item, index) => {
        let cleanDescription = '';
        if (typeof item.description === 'string') {
          cleanDescription = item.description
            .replace(/\\r\\n/g, '<br />')
            .replace(/\\n/g, '<br />');
        } else if (
          item.description !== null &&
          item.description !== undefined
        ) {
          cleanDescription = String(item.description);
        }
        return {
          ...item,
          id: `template-${index}-${Date.now()}`,
          name: item.templateName,
          description: cleanDescription,
        };
      });
    } else {
      templates.value = [];
      isHasTemplate.value = false;
    }
  } catch (error) {
    console.error('Şablon açıklamaları yüklenemedi:', error);
    isHasTemplate.value = false;
    return [];
  }
};
const openSelectedTempModal = () => {
  selectDescriptionTempModalRef.value?.openModal();
};

const applyTemplateToEditor = (templateDescription) => {
  editorContent.value = templateDescription.description;
};

const getProductNutritions = async (categoryId) => {
  try {
    const response = await store.dispatch('products/getProductNutritions', {
      categoryId: categoryId,
    });
    isNutritionInfoRequired.value = response.nutritionInfoRequired;
  } catch (error) {
    return error;
  }
};

watch(categoryId, (newId) => {
  if (newId) getProductNutritions(newId);
});

onMounted(async () => {
  await getExchanges();
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables';
@import '@/assets/styles/utilities';

.flex-container {
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  margin-bottom: 80px;
  gap: 16px;

  .page-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    gap: 12px;

    .page-title {
      color: var(--Neutral---Background-N80, #242424);
      font-size: 20px;
      font-weight: 700;
      line-height: 26px;
    }

    .watch-link {
      display: flex;
      height: 42px;
      align-items: center;
      justify-content: center;
      padding: 12px 24px;
      border: 0.5px solid #b3bbcb;
      border-radius: 6px;
      background: $colorN00;
      color: $colorN80;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: -0.05px;
      line-height: 18px;

      img {
        margin-right: 4px;
      }
    }
  }

  .page-description {
    color: $colorN80;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;

    span {
      font-weight: 600;
    }
  }

  .loading {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1183/518;
  }

  .seperator {
    width: 100%;
    height: 0.5px;
    margin: 16px 0;
    background-color: $colorD30;
  }

  .main-area {
    display: flex;
    width: 100%;
    flex-direction: column;
    padding-bottom: 24px;
    background-color: white;
  }

  .filter-area-top {
    display: flex;
    justify-content: space-between;
    background: $colorN00;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    gap: 16px;

    &.header-tab {
      padding: 24px 24px 0;
    }

    .periods {
      display: flex;
      gap: 12px;

      .tab {
        display: flex;
        width: 166px;
        height: 42px;
        align-items: center;
        justify-content: center;
        border: 0.5px solid $colorD30;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: -0.05px;
        line-height: 18px;

        &.table-tabs {
          width: 190px;
          padding: 0 12px;
          font-size: 13px;
        }

        &.selected {
          border: 2px solid $colorM50;
          background: $colorM05;
          color: $colorM50;
          font-weight: 700;
        }
      }
    }
  }

  .list-area {
    padding: 0 24px;
    border-radius: 8px;
  }

  & > .actions-pane {
    position: fixed;
    z-index: 10;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: end;
    padding: 20px;
    border-top: 0.5px solid $colorN30;
    background: $colorN00;

    button {
      display: inline-flex;
      width: auto;
      align-self: flex-end;
    }
  }
}
</style>
